import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, BookOpen, GraduationCap, Landmark, Presentation } from "lucide-react";
import { useI18n, useSlotText } from "@/i18n/translations";
import { disciplines } from "@/data/disciplines";
import { home } from "@/data/content";
import { Seo } from "@/components/Seo";
import { DemoBadge, NoteCard, SlotText } from "@/components/Editorial";
import { HeroLine, Reveal } from "@/components/Reveal";

/* Visual provvisorio (architettura genovese) — sarà sostituito con materiali autentici del Centro */
const HERO_IMG =
  "https://thumb.wikimedia.org/wikipedia/commons/thumb/a/a4/Genova_Universit%C3%A0_di_Genova_Cortile_1.jpg/960px-Genova_Universit%C3%A0_di_Genova_Cortile_1.jpg";

const heroLines = [
  { text: "Centro Studi", cls: "text-ink" },
  { text: "Pedagogici", cls: "text-ink" },
  { text: "Don Lorenzo Milani", cls: "italic text-bordeaux" },
];

/* Le quattro attività del Centro, come nel sottomenu del menu di navigazione */
const ATTIVITA_HOME = [
  { path: "/attivita/iniziative", key: "nav.att_iniziative", descKey: "att.cat_iniziative_desc", Icona: Landmark, testid: "home-att-iniziative" },
  { path: "/attivita/lectiones", key: "nav.att_lectiones", descKey: "att.cat_lectiones_desc", Icona: GraduationCap, testid: "home-att-lectiones" },
  { path: "/attivita/master", key: "nav.att_master", descKey: "att.cat_master_desc", Icona: Presentation, testid: "home-att-master" },
  { path: "/attivita/pubblicazioni", key: "nav.att_pubblicazioni", descKey: "att.cat_pubblicazioni_desc", Icona: BookOpen, testid: "home-att-pubblicazioni" },
];

export const Home = () => {
  const { t, lang } = useI18n();
  const slotText = useSlotText();
  const heroRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const wmY = useTransform(scrollYProgress, [0, 1], [0, -40]);

  return (
    <>
      <Seo titleKey="seo.home.title" descKey="seo.home.desc" path="/" />

      {/* HERO — reveal cinetico riga-per-riga + parallasse dell'immagine */}
      <section ref={heroRef} className="relative overflow-hidden" data-testid="home-hero">
        <motion.span
          aria-hidden="true"
          style={{ y: wmY }}
          className="pointer-events-none absolute -bottom-10 right-0 select-none font-serif text-[11rem] italic leading-none text-ink/[0.04] xl:text-[16rem]"
        >
          C.
        </motion.span>
        <div className="container-x grid items-center gap-12 py-16 sm:py-20 lg:grid-cols-12 lg:py-24">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.05 }}
              className="eyebrow mb-6 flex items-center gap-3 text-bordeaux"
            >
              <span className="inline-block h-px w-10 bg-bordeaux" aria-hidden="true" />
              {t("hero.kicker")}
            </motion.p>
            <h1 className="font-serif text-4xl font-bold leading-[1.06] tracking-tight sm:text-5xl lg:text-7xl" data-testid="home-hero-title">
              {heroLines.map((l, i) => (
                <HeroLine key={l.text} index={i} className={l.cls}>
                  {l.text}
                </HeroLine>
              ))}
            </h1>
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="mt-8 max-w-xl"
            >
              <p className="text-base leading-relaxed text-mutedtx sm:text-lg">
                <SlotText slot={home.heroIntro} />
              </p>
              <DemoBadge className="mt-4" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.85 }}
              className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center"
            >
              <Link
                to="/ambiti-di-ricerca"
                data-testid="hero-cta-research"
                className="inline-flex items-center justify-center gap-2 bg-ink px-7 py-3.5 text-[13px] font-semibold uppercase tracking-[0.12em] text-paper transition-colors duration-300 hover:bg-bordeaux"
              >
                {t("hero.cta1")}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <Link
                to="/contatti"
                data-testid="hero-cta-contact"
                className="inline-flex items-center justify-center gap-2 border border-ink/25 px-7 py-3.5 text-[13px] font-semibold uppercase tracking-[0.12em] text-ink transition-colors duration-300 hover:border-bordeaux hover:text-bordeaux"
              >
                {t("hero.cta2")}
              </Link>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, clipPath: "inset(0 0 100% 0)" }}
            animate={{ opacity: 1, clipPath: "inset(0 0 0% 0)" }}
            transition={{ duration: 1.1, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="relative lg:col-span-5"
          >
            <motion.div style={{ y: imgY }} className="relative">
              <div className="border border-ink/40 p-2 sm:p-3">
                <div className="relative overflow-hidden">
                  <img
                    src={HERO_IMG}
                    alt="Cortile del Palazzo dell'Università di Genova — visual provvisorio in attesa di materiali autentici del Centro"
                    style={{ filter: "saturate(0.5) contrast(0.85) brightness(1.12)" }}
                    className="img-treated aspect-[4/5] w-full object-cover"
                    loading="eager"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-paper/35" aria-hidden="true" />
                  <div className="pointer-events-none absolute inset-0 border border-ink/25" aria-hidden="true" />
                </div>
              </div>
              <p className="mt-5 text-xs italic text-mutedtx">
                {slotText({ it: "Visual provvisorio — in attesa di immagini autentiche del Centro", en: "Provisional visual — awaiting authentic images from the Centro" }).text}
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE — le 13 discipline in un nastro editoriale lento */}
      <div className="marquee overflow-hidden border-y border-borderc bg-cardwhite py-4" data-testid="home-marquee">
        <div className="marquee-track">
          {[0, 1].map((copy) => (
            <div key={copy} aria-hidden={copy === 1} className="flex shrink-0 items-center">
              {disciplines.map((d) => (
                <span key={`${copy}-${d.n}`} className="flex items-center text-[13px] uppercase tracking-[0.18em] text-ink/70">
                  <span className="mx-4 font-serif text-base italic normal-case tracking-normal text-bordeaux">
                    {String(d.n).padStart(2, "0")}
                  </span>
                  <span className="whitespace-nowrap">{lang === "en" && d.en ? d.en : d.it}</span>
                  <span className="mx-6 h-1 w-1 rounded-full bg-bordeaux/50" aria-hidden="true" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* IN EVIDENZA — aggiornamento dimostrativo, da sostituire con le comunicazioni reali del Centro */}
      <section className="container-x py-20 sm:py-24" data-testid="in-evidenza-container">
        <Reveal>
          <p className="eyebrow mb-4 flex items-center gap-3 text-bordeaux">
            <span className="inline-block h-px w-10 bg-bordeaux" aria-hidden="true" />
            {t("home.in_evidenza")}
          </p>
          <Link
            to="/attivita/iniziative#iniziative-in-corso"
            data-testid="in-evidenza-demo-card"
            className="group flex flex-col gap-8 border border-borderc bg-cardwhite p-8 transition-colors hover:border-bordeaux/60 sm:flex-row sm:items-center sm:p-10"
          >
            <div className="flex aspect-[4/5] w-28 shrink-0 items-center justify-center border border-ink/30 bg-[#FAF7F1] sm:w-32">
              <Presentation className="h-12 w-12 text-ink transition-transform duration-300 group-hover:scale-105" strokeWidth={1.5} aria-hidden="true" />
            </div>
            <div>
              <p className="eyebrow text-bordeaux">{t("home.evidenza_demo_kicker")}</p>
              <h2 className="mt-3 font-serif text-2xl font-semibold tracking-tight text-ink transition-colors group-hover:text-bordeaux">{t("home.evidenza_demo_title")}</h2>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-bodytx/85">{t("home.evidenza_demo_text")}</p>
              <DemoBadge className="mt-4" />
            </div>
          </Link>
          <div className="mt-6">
            <NoteCard title={t("att.nota_title")}>{t("home.evidenza_note")}</NoteCard>
          </div>
        </Reveal>
      </section>

      {/* LE ATTIVITÀ DEL CENTRO — quattro direzioni, come nel sottomenu di Attività */}
      <section className="container-x pb-20 sm:pb-24" data-testid="home-attivita">
        <Reveal className="mb-10">
          <p className="eyebrow mb-4 flex items-center gap-3 text-bordeaux">
            <span className="inline-block h-px w-10 bg-bordeaux" aria-hidden="true" />
            {t("home.att_kicker")}
          </p>
          <h2 className="font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl">{t("home.att_title")}</h2>
        </Reveal>
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
          {ATTIVITA_HOME.map(({ path, key, descKey, Icona, testid }, i) => (
            <Reveal key={path} delay={i * 0.08}>
              <Link to={path} className="group block" data-testid={testid}>
                <div className="relative flex aspect-[4/5] items-center justify-center overflow-hidden border border-ink/20 bg-transparent transition-colors duration-300 group-hover:bg-cardwhite">
                  <div className="absolute inset-3 border border-ink/30" aria-hidden="true" />
                  <div className="absolute inset-[18px] border border-ink/15" aria-hidden="true" />
                  <div className="relative flex flex-col items-center gap-4 px-6 text-center">
                    <Icona className="h-20 w-20 text-ink transition-transform duration-500 group-hover:scale-105" strokeWidth={1.5} aria-hidden="true" />
                    <span className="font-serif text-xl font-bold tracking-tight text-ink">CESP</span>
                  </div>
                </div>
                <h3 className="mt-5 font-serif text-xl font-medium leading-snug text-ink transition-colors duration-300 group-hover:text-bordeaux">{t(key)}</h3>
                <p className="mt-1.5 text-[13px] leading-relaxed text-mutedtx">{t(descKey)}</p>
                <span className="mt-3 inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.12em] text-bordeaux">
                  {t("common.esplora")}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-10">
          <Link
            to="/attivita"
            data-testid="home-attivita-all-link"
            className="link-underline inline-flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.12em] text-bordeaux"
          >
            {t("home.attivita_tutte")}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </Reveal>
      </section>

      {/* INDICE DELLE 13 DISCIPLINE */}
      <section className="container-x pb-20 sm:pb-24">
        <Reveal className="mb-10 grid gap-6 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <p className="eyebrow mb-4 flex items-center gap-3 text-bordeaux">
              <span className="inline-block h-px w-10 bg-bordeaux" aria-hidden="true" />
              {t("home.ambiti_kicker")}
            </p>
            <h2 className="font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl">{t("home.ambiti_title")}</h2>
          </div>
          <div className="lg:col-span-8">
            <p className="max-w-2xl leading-relaxed text-mutedtx">
              <SlotText slot={home.ambitiIntro} />
            </p>
            <DemoBadge className="mt-4" />
          </div>
        </Reveal>
        <div className="grid border-t border-borderc md:grid-cols-2 md:gap-x-16" data-testid="home-disciplines-index">
          {disciplines.map((d, i) => (
            <Reveal key={d.n} delay={Math.min(i * 0.04, 0.3)} y={16}>
              <Link
                to="/ambiti-di-ricerca"
                className="group flex items-baseline gap-5 border-b border-borderc py-4 pr-2 transition-colors hover:bg-cardwhite"
                data-testid={`home-discipline-item-${d.n}`}
              >
                <span className="font-serif text-sm italic text-bordeaux">{String(d.n).padStart(2, "0")}</span>
                <span className="font-serif text-lg text-ink transition-transform duration-300 group-hover:translate-x-1.5">{lang === "en" && d.en ? d.en : d.it}</span>
                <ArrowUpRight className="ml-auto h-4 w-4 shrink-0 text-bordeaux opacity-0 transition-opacity duration-300 group-hover:opacity-100" aria-hidden="true" />
              </Link>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-8">
          <Link
            to="/ambiti-di-ricerca"
            data-testid="home-ambiti-all-link"
            className="link-underline inline-flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.12em] text-bordeaux"
          >
            {t("home.ambiti_all")}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </Reveal>
      </section>

      {/* TEASER STORIA */}
      <section className="border-y border-borderc bg-cardwhite">
        <div className="container-x grid gap-10 py-20 sm:py-24 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <p className="eyebrow mb-4 flex items-center gap-3 text-bordeaux">
              <span className="inline-block h-px w-10 bg-bordeaux" aria-hidden="true" />
              {t("home.storia_kicker")}
            </p>
            <h2 className="font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl">{t("chi.storia_title")}</h2>
            <Link
              to="/chi-siamo"
              data-testid="home-storia-link"
              className="link-underline mt-6 inline-flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.12em] text-bordeaux"
            >
              {t("home.storia_link")}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-8">
            <blockquote className="font-serif text-xl italic leading-relaxed text-ink/90 sm:text-2xl">
              <SlotText slot={home.storiaQuote} />
            </blockquote>
            <DemoBadge label={t("storia.bozza")} className="mt-5" />
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Home;
